// SimulationTab — Phase 1: What-If Simulation Engine
// Sub-tabs: What-If | Sensitivity | Saved Simulations
import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BarChartIcon from '@mui/icons-material/BarChart';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { WhatIfSimulator } from '../components/WhatIfSimulator';
import { SensitivityTornado } from '../components/SensitivityTornado';
import { bomIntelligenceService } from '../../../services/bomIntelligenceService';
import type {
  SensitivityAnalysisResponse,
  SensitivityVariable,
  SimulationSummary,
} from '../../../services/bomIntelligenceService';

interface SimulationTabProps {
  bomId: string;
  onViewPart?: (partId: string) => void;
}

const SENSITIVITY_PRESETS: { label: string; variables: SensitivityVariable[] }[] = [
  {
    label: 'Cost Drivers',
    variables: [
      { type: 'PRICE_PERCENT', scope: 'GLOBAL', label: 'Material Prices' },
      { type: 'LABOR_RATE', scope: 'GLOBAL', label: 'Labor Rate' },
      { type: 'OVERHEAD_PERCENT', scope: 'GLOBAL', label: 'Overhead %' },
      { type: 'QUANTITY_MULTIPLIER', scope: 'GLOBAL', label: 'Order Quantity' },
    ],
  },
  {
    label: 'Supply Chain',
    variables: [
      { type: 'PRICE_PERCENT', scope: 'GLOBAL', label: 'Material Prices' },
      { type: 'LEAD_TIME_PERCENT', scope: 'GLOBAL', label: 'Lead Times' },
      { type: 'QUANTITY_MULTIPLIER', scope: 'GLOBAL', label: 'Order Quantity' },
    ],
  },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

export const SimulationTab: React.FC<SimulationTabProps> = ({ bomId, onViewPart }) => {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab icon={<PlayArrowIcon />} label="What-If" iconPosition="start" />
        <Tab icon={<BarChartIcon />} label="Sensitivity" iconPosition="start" />
        <Tab icon={<BookmarkIcon />} label="Saved" iconPosition="start" />
      </Tabs>

      {subTab === 0 && (
        <WhatIfSimulator bomId={bomId} onViewPart={onViewPart} />
      )}
      {subTab === 1 && (
        <SensitivityPanel bomId={bomId} />
      )}
      {subTab === 2 && (
        <SavedSimulationsPanel bomId={bomId} />
      )}
    </Box>
  );
};

// ============================================
// SENSITIVITY PANEL
// ============================================

const SensitivityPanel: React.FC<{ bomId: string }> = ({ bomId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SensitivityAnalysisResponse | null>(null);
  const [selectedPreset, setSelectedPreset] = useState(0);
  const [targetMetric, setTargetMetric] = useState<'TOTAL_COST' | 'READINESS_SCORE'>('TOTAL_COST');

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const preset = SENSITIVITY_PRESETS[selectedPreset];
      const response = await bomIntelligenceService.runSensitivityAnalysis({
        bomId,
        targetMetric,
        variables: preset.variables,
      });
      setResult(response);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  }, [bomId, selectedPreset, targetMetric]);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Sensitivity Analysis Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Identify which variables have the highest impact on your target metric.
          Each variable is swept across -20% to +20% and the impact is measured.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Variable Preset</InputLabel>
            <Select
              value={selectedPreset}
              label="Variable Preset"
              onChange={(e) => setSelectedPreset(Number(e.target.value))}
            >
              {SENSITIVITY_PRESETS.map((p, idx) => (
                <MenuItem key={idx} value={idx}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Target Metric</InputLabel>
            <Select
              value={targetMetric}
              label="Target Metric"
              onChange={(e) => setTargetMetric(e.target.value as any)}
            >
              <MenuItem value="TOTAL_COST">Total Cost</MenuItem>
              <MenuItem value="READINESS_SCORE">Readiness Score</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <BarChartIcon />}
            onClick={runAnalysis}
            disabled={loading}
          >
            {loading ? 'Analyzing...' : 'Run Analysis'}
          </Button>
        </Stack>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {SENSITIVITY_PRESETS[selectedPreset]?.variables.map((v, idx) => (
            <Chip key={idx} label={v.label} size="small" variant="outlined" />
          ))}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <>
          <SensitivityTornado
            variables={result.variables}
            targetMetric={targetMetric}
            baselineValue={
              targetMetric === 'TOTAL_COST'
                ? result.baselineMetrics.totalCost
                : result.baselineMetrics.readinessScore
            }
          />
          {result.durationMs && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Analysis completed in {result.durationMs}ms ({result.variables.length} variables x 5 levels = {result.variables.length * 5} simulations)
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

// ============================================
// SAVED SIMULATIONS PANEL
// ============================================

const SavedSimulationsPanel: React.FC<{ bomId: string }> = ({ bomId }) => {
  const [simulations, setSimulations] = useState<SimulationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSimulations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bomIntelligenceService.listSimulations(bomId);
      setSimulations(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bomId]);

  useEffect(() => {
    loadSimulations();
  }, [loadSimulations]);

  const handleDelete = useCallback(async (simulationId: string) => {
    try {
      await bomIntelligenceService.deleteSimulation(bomId, simulationId);
      setSimulations((prev) => prev.filter((s) => s.simulationId !== simulationId));
    } catch (err: any) {
      setError(err.message);
    }
  }, [bomId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (simulations.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No saved simulations yet. Run a What-If or Sensitivity analysis and save it to see it here.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <List>
        {simulations.map((sim) => (
          <React.Fragment key={sim.simulationId}>
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight={500}>{sim.name}</Typography>
                    <Chip
                      label={sim.simulationType}
                      size="small"
                      color={sim.simulationType === 'WHAT_IF' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                    <Chip
                      label={`${sim.adjustmentCount} adjustments`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" component="span">
                      Cost: {formatCurrency(sim.baselineMetrics?.totalCost || 0)} {' '}
                      {sim.simulatedMetrics && (
                        <Typography
                          component="span"
                          variant="body2"
                          color={sim.simulatedMetrics.deltaCost > 0 ? 'error.main' : 'success.main'}
                        >
                          ({sim.simulatedMetrics.deltaCost > 0 ? '+' : ''}
                          {formatCurrency(sim.simulatedMetrics.deltaCost)})
                        </Typography>
                      )}
                    </Typography>
                    {' | '}
                    <Typography variant="body2" component="span">
                      {sim.itemsChanged} items changed
                    </Typography>
                    {' | '}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(sim.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleDelete(sim.simulationId)}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default SimulationTab;
